import type { SocketType } from "../../types";
import { dataMappingColumnSocketId } from "../../steps/data-access/use-data-mapping.step";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { prettify } from "./socketDefinition";
import { statefulTableColumnTypeToSocketType } from "./statefulTableSocketTypes";

/**
 * The snapshot is persisted workflow config — normalize rather than trust its
 * shape, so a malformed entry degrades to the `any` fallback instead of
 * crashing socket generation.
 */
function snapshotColumns(
  config: Record<string, unknown>,
): Array<{ name: string; type: string }> {
  return (Array.isArray(config.columns) ? config.columns : []).filter(
    (column): column is { name: string; type: string } =>
      typeof column === "object" &&
      column !== null &&
      typeof (column as { name?: unknown }).name === "string" &&
      typeof (column as { type?: unknown }).type === "string",
  );
}

/**
 * One required input socket per configured match column for
 * `use_data_mapping`, each typed from the design-time `columns` snapshot.
 *
 * The whole point of the step is that picking a column set retypes the inputs,
 * so the socket types are not a nicety — a mapping keyed on an integer column
 * wired from a string producer is a bug the canvas should refuse. When no set
 * is picked yet (fresh palette drop) a single `any` placeholder shows where
 * the inputs will appear: no sockets would leave a node with no input at all,
 * and guessing `string` would silently accept the wrong wire.
 *
 * Runtime does NOT trust this snapshot on faith — the handler re-reads the
 * mapping's live schema and throws `DataMappingSchemaDriftError` if a column's
 * type has moved since.
 */
export function generateUseDataMappingInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const columns = snapshotColumns(config);
  const matchColumns = Array.isArray(config.matchColumns)
    ? config.matchColumns.filter(
        (column): column is string =>
          typeof column === "string" && column.length > 0,
      )
    : [];

  if (matchColumns.length === 0) {
    return [
      {
        id: "matchValue",
        name: "Match Value",
        type: "any" as SocketType,
        required: true,
        description:
          "Value to match — pick a column set in the step config to create the typed inputs",
      },
    ];
  }

  return matchColumns.map((matchColumn) => {
    const matched = columns.find(
      (column) => column.name.toLowerCase() === matchColumn.toLowerCase(),
    );
    const type = matched
      ? statefulTableColumnTypeToSocketType(matched.type)
      : ("any" as SocketType);
    return {
      // Prefixed, not the bare column name: socket ids double as payload
      // keys, and column names are user data — see dataMappingColumnSocketId.
      id: dataMappingColumnSocketId(matchColumn),
      name: prettify(matchColumn),
      type,
      required: true,
      description: `Value to match against column "${matchColumn}"`,
    };
  });
}

/**
 * Output sockets for `use_data_mapping`. `requireSingleMatch` decides the shape:
 * a list of matching rows, or the one row — and in single-match mode the row is
 * also fanned into one typed socket per column (the `stateful_table_lookup_row`
 * pattern), so downstream wires a value directly instead of json-pathing.
 * Column sockets are `col_`-prefixed (see `dataMappingColumnSocketId`) so
 * user-named columns can never collide with the step's own `found`/`row` keys.
 *
 * Only the applicable row shape is published, not both — a node that offers
 * `rows` and `row` invites wiring the one the step will never emit, and the
 * whole reason the shape is config-driven is that the author has already said
 * which they want. Per-column sockets exist only in single-match mode: a list
 * has no single value per column to offer.
 *
 * Deliberately no `matchCount`, `candidateKeys`, or drift socket: nothing
 * downstream branches on them, and the ambiguity detail belongs in the item
 * error and the log, where the operator reads it.
 */
export function generateUseDataMappingOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const single = config.requireSingleMatch === true;
  const fixed: DynamicSocketDefinition[] = [
    {
      id: "found",
      name: "Found",
      type: "boolean" as SocketType,
      required: true,
      description: single
        ? "A single row matched. False when nothing matched (an ordinary outcome)."
        : "At least one row matched",
    },
    single
      ? {
          id: "row",
          name: "Row",
          type: "json" as SocketType,
          required: false,
          description:
            "The matching row, including the mapping's key columns. Empty when nothing matched.",
        }
      : {
          id: "rows",
          name: "Rows",
          type: "array<json>" as SocketType,
          required: false,
          description:
            "Every matching row, each including the mapping's key columns. Empty when nothing matched.",
        },
  ];
  if (!single) return fixed;

  const columnSockets = snapshotColumns(config).map((column) => ({
    id: dataMappingColumnSocketId(column.name),
    name: prettify(column.name),
    type: statefulTableColumnTypeToSocketType(column.type),
    required: false,
    description: `Value of column "${column.name}" from the matched row. Empty when nothing matched.`,
  }));
  return [...fixed, ...columnSockets];
}
